import _regeneratorRuntime from "@babel/runtime/helpers/regeneratorRuntime";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _asyncToGenerator from "@babel/runtime/helpers/asyncToGenerator";
import _objectSpread from "@babel/runtime/helpers/objectSpread2";
import _isEmpty from "lodash/isEmpty";
import { useEffect } from "react";
import { useAuth } from "@crehana/auth";
import { useTranslation } from "@crehana/i18n";
// biome-ignore lint/style/noRestrictedImports: legacy import, will be migrated
import { Checkbox, PrimaryButton, TextField } from "@crehana/ui";
import { getQueryParam } from "@crehana/utils";
import { Controller, useForm } from "react-hook-form/dist/index.ie11";
import { useContactData } from "../../../hooks/useContactData";
import { PhoneInput } from "../../../PhoneInput";
import { validations } from "../../constants";
import { getNextUrl, sendRegisterTrackToGoogleAdsAndAnalytics } from "../../utils";
import LegalCheckbox, { LegalErrorMessage, rulesForRHFControllerComponent } from "../LegalCheckbox";
import { useVerifyUserQuery } from "./hooks";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
var Signup = function Signup(_ref) {
  var _watch$phone;
  var onSuccess = _ref.onSuccess,
    referFriendId = _ref.referFriendId,
    defaultEmailProp = _ref.defaultEmail,
    isDark = _ref.isDark,
    nextUrlProp = _ref.nextUrl,
    _ref$canShowPhoneInpu = _ref.canShowPhoneInput,
    canShowPhoneInput = _ref$canShowPhoneInpu === void 0 ? false : _ref$canShowPhoneInpu;
  var _useTranslation = useTranslation(),
    t = _useTranslation.t,
    countryCode = _useTranslation.countryCode;
  var _useAuth = useAuth(),
    register = _useAuth.register;
  var nextUrl = nextUrlProp || getNextUrl();
  var contactSourceEnum = getQueryParam('contact_source_enum') || '';
  var _useVerifyUserQuery = useVerifyUserQuery(),
    validateEmailQuery = _useVerifyUserQuery.validateEmailQuery;
  var _useContactData = useContactData(),
    saveContactData = _useContactData.saveContactData;
  var defaultEmail = getQueryParam('email_user') || defaultEmailProp;
  var _useForm = useForm({
      mode: 'onChange'
    }),
    handleSubmit = _useForm.handleSubmit,
    errors = _useForm.errors,
    control = _useForm.control,
    formState = _useForm.formState,
    setError = _useForm.setError,
    watch = _useForm.watch,
    reset = _useForm.reset;

  /*
    This is very important to make the default values conditional.
    ReactHookForm caches the default values on the first render, so
    it's not possible to set conditional defaultValues there.
    More info at: https://react-hook-form.com/api/useform
  */
  useEffect(function () {
    var getDefaultValues = function getDefaultValues(params) {
      var _defaultValues = {
        email: params.defaultEmail || '',
        password: '',
        checkboxLegal: true,
        newsLetterCheckbox: true
      };

      // biome-ignore lint/style/useBlockStatements: single-line early return
      if (!params.canShowPhoneInput) return _objectSpread(_objectSpread({}, _defaultValues), {}, {
        phone: {
          code: countryCode || undefined
        }
      });
      return _defaultValues;
    };
    reset(getDefaultValues({
      defaultEmail: defaultEmail,
      canShowPhoneInput: canShowPhoneInput
    }));
  }, [defaultEmail, canShowPhoneInput, countryCode, reset]);
  var friendInvitationId = getQueryParam('friend_invitation_id');
  var validateIfCurrentEmailIsAlreadyRegistered = /*#__PURE__*/function () {
    var _ref2 = _asyncToGenerator(/*#__PURE__*/_regeneratorRuntime().mark(function _callee(value) {
      var _res$data;
      var res, _res$data2, _ref3, messages, success, _ref4, _ref5, first;
      return _regeneratorRuntime().wrap(function _callee$(_context) {
        while (1) switch (_context.prev = _context.next) {
          case 0:
            _context.next = 2;
            return validateEmailQuery({
              value: value
            });
          case 2:
            res = _context.sent;
            if (!(res !== null && res !== void 0 && (_res$data = res.data) !== null && _res$data !== void 0 && _res$data.verifyUser)) {
              _context.next = 9;
              break;
            }
            _ref3 = (res === null || res === void 0 ? void 0 : (_res$data2 = res.data) === null || _res$data2 === void 0 ? void 0 : _res$data2.verifyUser) || {}, messages = _ref3.messages, success = _ref3.success; // biome-ignore lint/style/useBlockStatements: single-line early return
            if (!success) {
              _context.next = 7;
              break;
            }
            return _context.abrupt("return", true);
          case 7:
            _ref4 = messages || [], _ref5 = _slicedToArray(_ref4, 1), first = _ref5[0];
            return _context.abrupt("return", t('AUTH_VALIDATION_EMAIL_IN_USE', (first === null || first === void 0 ? void 0 : first.message) || ''));
          case 9:
          case "end":
            return _context.stop();
        }
      }, _callee);
    }));
    return function validateIfCurrentEmailIsAlreadyRegistered(_x) {
      return _ref2.apply(this, arguments);
    };
  }();
  var onSubmit = handleSubmit(/*#__PURE__*/function () {
    var _ref6 = _asyncToGenerator(/*#__PURE__*/_regeneratorRuntime().mark(function _callee2(data) {
      var _res$data3;
      var email, password, phone, newsLetterCheckbox, res, _ref7, messages, success, redirect, user, _ref8, _ref9, first;
      return _regeneratorRuntime().wrap(function _callee2$(_context2) {
        while (1) switch (_context2.prev = _context2.next) {
          case 0:
            email = data.email, password = data.password, phone = data.phone, newsLetterCheckbox = data.newsLetterCheckbox;
            _context2.next = 3;
            return register({
              email: email,
              password: password,
              nextUrl: friendInvitationId ? '/premium/' : nextUrl,
              referFriendId: referFriendId === null || referFriendId === void 0 ? void 0 : referFriendId.toString(),
              friendInvitationId: friendInvitationId === null || friendInvitationId === void 0 ? void 0 : friendInvitationId.toString(),
              newsLetterCheckbox: newsLetterCheckbox
            });
          case 3:
            res = _context2.sent;
            _ref7 = (res === null || res === void 0 ? void 0 : (_res$data3 = res.data) === null || _res$data3 === void 0 ? void 0 : _res$data3.createUser) || {}, messages = _ref7.messages, success = _ref7.success, redirect = _ref7.redirect, user = _ref7.user;
            if (!(phone !== null && phone !== void 0 && phone.isValid && !_isEmpty(contactSourceEnum) && typeof contactSourceEnum === 'string')) {
              _context2.next = 8;
              break;
            }
            _context2.next = 8;
            return saveContactData({
              // @ts-expect-error
              source: contactSourceEnum,
              phoneNumber: phone.fullNumber,
              email: email
            });
          case 8:
            if (success) {
              _context2.next = 12;
              break;
            }
            _ref8 = messages || [], _ref9 = _slicedToArray(_ref8, 1), first = _ref9[0];
            if (first !== null && first !== void 0 && first.message) {
              setError('email', {
                type: '',
                message: first.message
              });
            }
            return _context2.abrupt("return");
          case 12:
            sendRegisterTrackToGoogleAdsAndAnalytics();
            onSuccess({
              user: user,
              redirect: redirect,
              isNewUser: true
            });
          case 14:
          case "end":
            return _context2.stop();
        }
      }, _callee2);
    }));
    return function (_x2) {
      return _ref6.apply(this, arguments);
    };
  }());
  var isSubmitting = formState.isSubmitting,
    isValid = formState.isValid;
  var isPhoneValid = canShowPhoneInput ? (_watch$phone = watch().phone) === null || _watch$phone === void 0 ? void 0 : _watch$phone.isValid : true;
  var isDisabled = isSubmitting || !isValid || !isPhoneValid;
  return /*#__PURE__*/_jsxs("form", {
    onSubmit: onSubmit,
    className: "mb-32",
    children: [/*#__PURE__*/_jsx("div", {
      children: /*#__PURE__*/_jsx(Controller, {
        control: control,
        name: "email",
        rules: {
          required: validations.required(t('INPUT_REQUIRED', {
            defaultValue: 'Campo requerido'
          })),
          pattern: validations.email(t('VALIDATE_FORMAT_EMAIL', {
            defaultValue: 'Por favor, ingresa un email válido.'
          })),
          validate: validateIfCurrentEmailIsAlreadyRegistered
        },
        defaultValue: defaultEmail,
        render: function render(_ref0) {
          var _errors$email, _errors$email2;
          var onBlur = _ref0.onBlur,
            onChange = _ref0.onChange,
            _ref0$value = _ref0.value,
            value = _ref0$value === void 0 ? '' : _ref0$value;
          return /*#__PURE__*/_jsx(TextField, {
            value: value,
            onChange: onChange,
            onBlur: onBlur
            // it's ok to force the focus on this form field
            // eslint-disable-next-line jsx-a11y/no-autofocus
            ,

            autoFocus: true,
            full: true,
            type: "email",
            id: "email",
            maxLength: 50,
            label: t('EMAIL', 'Email'),
            disabled: isSubmitting,
            "data-testid": "email-field",
            darkMode: isDark,
            className: "mb-16",
            error: !!errors.email,
            errorText: (_errors$email = errors.email) !== null && _errors$email !== void 0 && _errors$email.message ? t((_errors$email2 = errors.email) === null || _errors$email2 === void 0 ? void 0 : _errors$email2.message) : undefined
          });
        }
      })
    }), /*#__PURE__*/_jsxs("div", {
      className: "w-full",
      children: [/*#__PURE__*/_jsx(Controller, {
        control: control,
        name: "password",
        rules: {
          required: validations.required(t('INPUT_REQUIRED', {
            defaultValue: 'Campo requerido'
          })),
          minLength: validations.minLength(t('VALIDATE_MIN_PASSWORD', {
            defaultValue: 'Tu contraseña debe de tener al menos 6 caracteres'
          })),
          maxLength: validations.maxLength(t('VALIDATE_MAX_PASSWORD', {
            defaultValue: 'Tu contraseña debe de tener máximo 6 caracteres'
          }))
        },
        render: function render(_ref1) {
          var _errors$password, _errors$password2;
          var onBlur = _ref1.onBlur,
            onChange = _ref1.onChange,
            _ref1$value = _ref1.value,
            value = _ref1$value === void 0 ? '' : _ref1$value;
          return /*#__PURE__*/_jsx(TextField, {
            value: value,
            onChange: onChange,
            onBlur: onBlur,
            full: true,
            label: t('PASSWORD', 'Contraseña'),
            type: "password",
            id: "password",
            maxLength: 20,
            disabled: isSubmitting,
            "data-testid": "password-field",
            darkMode: isDark,
            className: "mb-16",
            error: !!errors.password,
            errorText: (_errors$password = errors.password) !== null && _errors$password !== void 0 && _errors$password.message ? t((_errors$password2 = errors.password) === null || _errors$password2 === void 0 ? void 0 : _errors$password2.message) : undefined
          });
        }
      }), canShowPhoneInput && /*#__PURE__*/_jsx("div", {
        className: "w-full",
        children: /*#__PURE__*/_jsx(Controller, {
          control: control,
          name: "phone",
          rules: {
            required: {
              value: true,
              message: t('INPUT_REQUIRED', 'Campo requerido')
            }
          },
          render: function render(_ref10) {
            var onChange = _ref10.onChange;
            return /*#__PURE__*/_jsx(PhoneInput, {
              className: "mb-16",
              label: t('PHONE', 'Teléfono'),
              showAll: true,
              isDarkMode: isDark,
              isRequired: true,
              onChange: onChange,
              defaultCountry: countryCode || undefined
            });
          }
        })
      })]
    }), /*#__PURE__*/_jsxs("div", {
      children: [/*#__PURE__*/_jsx(PrimaryButton, {
        className: "w-full",
        label: t('REGISTER', 'Regístrate'),
        type: "submit",
        "data-testid": "submit-button",
        "data-cy": "submit",
        size: "l",
        darkMode: isDark,
        disabled: isDisabled
      }), /*#__PURE__*/_jsxs("div", {
        className: "px-32 mt-32 sm:px-0",
        children: [/*#__PURE__*/_jsx(Controller, {
          control: control,
          name: "checkboxLegal",
          render: function render(_ref11) {
            var onChange = _ref11.onChange,
              value = _ref11.value;
            return /*#__PURE__*/_jsx(LegalCheckbox, {
              dataTestId: "legal-field",
              onChange: onChange,
              value: value,
              isDark: isDark
            });
          },
          rules: rulesForRHFControllerComponent(t)
        }), errors.checkboxLegal && /*#__PURE__*/_jsx(LegalErrorMessage, {
          message: errors.checkboxLegal.message
        }), /*#__PURE__*/_jsx("div", {
          className: "flex items-start mt-12",
          children: /*#__PURE__*/_jsx(Controller, {
            control: control,
            name: "newsLetterCheckbox",
            render: function render(_ref12) {
              var _onChange = _ref12.onChange,
                value = _ref12.value;
              return /*#__PURE__*/_jsxs("div", {
                children: [/*#__PURE__*/_jsx(Checkbox, {
                  small: true,
                  onChange: function onChange(_ref13) {
                    var target = _ref13.target;
                    var checked = target.checked;
                    _onChange(!checked);
                  },
                  checked: value
                }), /*#__PURE__*/_jsx("span", {
                  className: "ml-8 font-legals tw-font-semibold sm:text-left text-gray-dark",
                  children: t('CHECKBOX_DATA_USAGE', 'Autorizo que Crehana use los datos proporcionados para contactarme y recibir promociones, ofertas e información de nuevos productos.')
                })]
              });
            }
          })
        })]
      })]
    })]
  });
};
export default Signup;